import {
  type ClientEmployeesQuery,
  useClientEmployeesQuery,
} from "@graphql/client-employees/clientEmployees.g";
import { json2csv } from "json-2-csv";
import { useCallback, useMemo, useState } from "react";

import { queryClient } from "@providers/QueryClient/queryClient";
import { useTableColumnSettings } from "@providers/TableColumnSettingsProvider/useTableColumnSettings";

import {
  DEFAULT_CLIENT_EMPLOYEE_TABLE_COLUMN_SETTINGS,
} from "../UI/ClientEmployeesTableColumns";
import {
  CLIENT_EMPLOYEE_TABLE_COLUMN_LABELS,
  ClientEmployeeTableColumns,
} from "../UI/ClientEmployeesTableColumns";
import type { ClientEmployeeViewMode } from "../ClientEmployeesTable";

type Props = {
  tableId: string;
  clientId: string;
  exportType?: "csv";
  // Filter parameters - same as the table component
  nameSearch?: string;
  emailSearch?: string;
  viewMode?: ClientEmployeeViewMode;
};

export const useClientEmployeesTableExport = ({
  tableId,
  clientId,
  exportType = "csv",
  nameSearch,
  emailSearch,
  viewMode = "active",
}: Props) => {
  const [isExporting, setIsExporting] = useState(false);
  const { getColumnSettings, getColumnOrder } = useTableColumnSettings({
    tableId,
  });

  const exportData = useCallback(async () => {
    if (isExporting) return;

    setIsExporting(true);
    try {
      const BATCH_SIZE = 100;
      const allEmployees: NonNullable<
        ClientEmployeesQuery["clientEmployees"]
      >["edges"] = [];
      let hasNextPage = true;
      let offset = 0;

      const isArchivedView = viewMode === "archived";

      // Fetch data in batches of 100 records
      while (hasNextPage) {
        const queryVariables = {
          clientId,
          first: BATCH_SIZE,
          offset: offset,
          name_Icontains: nameSearch || undefined,
          email_Icontains: emailSearch || undefined,
          includeArchived: isArchivedView,
        };

        const data = await queryClient.fetchQuery({
          queryKey: useClientEmployeesQuery.getKey(queryVariables),
          queryFn: useClientEmployeesQuery.fetcher(queryVariables),
        });

        const edges = data.clientEmployees?.edges || [];
        allEmployees.push(...edges);

        // Check if we have more data to fetch
        hasNextPage = edges.length === BATCH_SIZE;
        offset += BATCH_SIZE;
      }

      const columnSettings =
        getColumnSettings() ?? DEFAULT_CLIENT_EMPLOYEE_TABLE_COLUMN_SETTINGS;
      const columnOrder = getColumnOrder() as
        | ClientEmployeeTableColumns[]
        | undefined;

      // Get enabled columns
      const enabledColumns = Object.entries(columnSettings)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .filter(([_, value]) => value)
        .map(([key]) => key as ClientEmployeeTableColumns);

      // Sort columns according to user's preferred order if available
      let selectedColumns: ClientEmployeeTableColumns[];
      if (columnOrder && columnOrder.length > 0) {
        // Use the user's column order, but only include enabled columns
        selectedColumns = columnOrder.filter((col) =>
          enabledColumns.includes(col),
        );

        // Add any enabled columns that aren't in the order (shouldn't happen, but safety check)
        const missingColumns = enabledColumns.filter(
          (col) => !selectedColumns.includes(col),
        );
        selectedColumns.push(...missingColumns);
      } else {
        // Fall back to default order
        selectedColumns = enabledColumns;
      }

      const employees = allEmployees
        .map((edge) => {
          if (!edge?.node) return null;

          const employee: Record<string, unknown> = {};

          selectedColumns.forEach((column) => {
            const label = CLIENT_EMPLOYEE_TABLE_COLUMN_LABELS[column];

            switch (column) {
              case ClientEmployeeTableColumns.name:
                employee[label] = edge.node?.name || "";
                break;
              case ClientEmployeeTableColumns.email:
                employee[label] = edge.node?.email || "";
                break;
              case ClientEmployeeTableColumns.appsUsed:
                employee[label] = edge.node?.resourceGroupCount || 0;
                break;
              case ClientEmployeeTableColumns.lastActivity:
                employee[label] = edge.node?.sessions?.aggregate?.max?.updatedAt
                  ? new Date(
                      edge.node.sessions.aggregate.max.updatedAt,
                    ).toLocaleDateString()
                  : "";
                break;
              case ClientEmployeeTableColumns.archivedAt:
                employee[label] = edge.node?.archivedAt
                  ? new Date(edge.node.archivedAt).toLocaleDateString()
                  : "";
                break;
              default:
                employee[label] = "";
            }
          });

          return employee;
        })
        .filter(
          (employee): employee is Record<string, unknown> => employee !== null,
        );

      if (!employees || employees.length === 0) {
        throw new Error("No client employees found");
      }

      if (exportType === "csv") {
        const csv = json2csv(employees);
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `client-employees-${new Date().toISOString().split("T")[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Export failed:", error);
      throw error;
    } finally {
      setIsExporting(false);
    }
  }, [
    exportType,
    getColumnSettings,
    getColumnOrder,
    clientId,
    nameSearch,
    emailSearch,
    viewMode,
    isExporting,
  ]);

  return useMemo(
    () => ({ exportData, isExporting }),
    [exportData, isExporting],
  );
};
